import { FeedEvents, trackEvent } from "@/analytics";

import { ComedianWithImageUrl } from "@/components/feed/types";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { Follow } from "@/components/checkout";
import { useHideComedian } from "@/hooks/hide/useHideComedian";
import Image from "next/image";
import Link from "next/link";
import { X } from "lucide-react";

interface SuggestedComedianCardProps {
  comedian: ComedianWithImageUrl;
  isLoggedOutState: boolean;
  hideEnabled?: boolean;
  onHide?: (comedianId: string) => void;
}

export default function SuggestedComedianCard({
  comedian,
  isLoggedOutState,
  hideEnabled = false,
  onHide,
}: SuggestedComedianCardProps) {
  const { hideComedian, isHiding } = useHideComedian();

  if (!comedian) return null;

  const redirectTo = isLoggedOutState ? `/${comedian.slug}` : "";

  const handleCardClick = () => {
    trackEvent(FeedEvents.CLICKED_SUGGESTED_COMEDIAN_CARD, {
      comedianId: comedian.id,
      comedianName: comedian.display_name,
      url: `/${comedian.slug}`,
    });
  };

  const handleHideClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isHiding) return;

    hideComedian({
      comedianId: comedian.id,
      location: "feed-suggested-comedian-carousel",
      onHide,
    });
  };

  return (
    <div className="relative flex flex-col min-w-0 flex-grow-0 flex-shrink-0 basis-[150px] md:basis-[200px] is-snapped is-in-view border border-[#E1E1E1] shadow-sm rounded-md overflow-clip">
      {hideEnabled && (
        <button
          type="button"
          onClick={handleHideClick}
          disabled={isHiding}
          className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-white/90 hover:bg-white border border-neutral-200 shadow-sm flex items-center justify-center transition-colors duration-200 hover:border-neutral-300 disabled:opacity-50"
          title="Hide comedian"
        >
          <X className="w-3.5 h-3.5 text-neutral-600" />
        </button>
      )}
      <Link
        href={`/${comedian.slug}`}
        className="aspect-square w-full bg-neutral-100 overflow-hidden"
        onClick={handleCardClick}
      >
        {comedian.imageUrl && (
          <Image
            src={comedian.imageUrl}
            alt={comedian.display_name ?? "Comedian"}
            height={200}
            width={200}
            className="object-cover w-full h-auto min-h-full aspect-square"
          />
        )}
      </Link>
      <div className="flex flex-col justify-between flex-1 gap-3 px-4 py-3">
        <Link href={`/${comedian.slug}`} onClick={handleCardClick}>
          <div className="flex flex-col justify-center items-center flex-1 gap-0.5 md:gap-1">
            <h4 className="text-[16px] font-medium leading-tight text-center line-clamp-1">
              {comedian.display_name}
            </h4>
            <p className="text-[12px] font-normal text-[#595959] leading-tight">
              Comedian
            </p>
          </div>
        </Link>

        <Follow
          isLoggedOutState={isLoggedOutState}
          redirectTo={redirectTo}
          buttonText="Subscribe"
          followedLocation={FOLLOW_LOCATIONS.FEED_SUGGESTED_COMEDIAN_CARD}
          comedianToFollow={comedian}
          customCSSButtonClass="text-primary bg-[#FFEDE6] font-medium text-[14px] py-1.5 w-full rounded-md transition-colors ease-in-out duration-200 hover:bg-[#fad9cc]"
          customCSSContainerClass="block"
          additionalEventToTrack={{
            event: FeedEvents.CLICKED_SUGGESTED_COMEDIAN_CARD,
            eventProperties: {
              comedianId: comedian.id,
              comedianName: comedian.display_name,
              url: `/${comedian.slug}`,
            },
          }}
        />
      </div>
    </div>
  );
}
